import { z } from "incur";
import { validateApiKey, apiBaseUrl } from "./client";
import {
	OAuthError,
	type OAuthLoginDependencies,
	type OAuthLoginResult,
	performOAuthLogin,
	prepareOAuthLogin,
} from "./oauth";
import {
	deriveProfileName,
	getActiveProfile,
	listProfiles,
	upsertProfile,
} from "./profiles";
import {
	chooseAuthMethod,
	PromptCancelledError,
	promptApiKey,
	startSpinner,
} from "./prompts";
import { AuthMethod, type CtaInput, type ErrorInput, Identity } from "./types";

/**
 * The `whop login` flow: schemas, the context-free `performLogin`, and the
 * incur run adapters. Shared by the `auth login` group command and the
 * top-level `whop login` alias (incur aliases can't cross from a group into a
 * top-level command).
 */

// --- Shared schemas ---
export const LoginMethod = z.enum(["oauth", "api-key"]);
export type LoginMethod = z.infer<typeof LoginMethod>;

export const loginOptions = z.object({
	method: LoginMethod.optional().describe("Authentication method to use"),
	apiKey: z
		.string()
		.optional()
		.describe("API key for non-interactive login (skips the prompt)"),
	profile: z
		.string()
		.optional()
		.describe("Name for this profile (defaults to the account route)"),
	accountId: z
		.string()
		.optional()
		.describe(
			"Business account to act on behalf of (biz_xxx) — for OAuth users who manage more than one",
		),
});

export const loginEnv = z.object({
	WHOP_API_KEY: z
		.string()
		.optional()
		.describe("API key for non-interactive login"),
	WHOP_API_BASE_URL: z
		.string()
		.optional()
		.describe("Override the Whop API base URL"),
	WHOP_CONFIG_DIR: z
		.string()
		.optional()
		.describe("Override the CLI config directory"),
});

const authenticatedLoginOutput = z.object({
	profile: z.string().describe("The profile name"),
	active: z.boolean().describe("Whether this profile is now the active one"),
	method: AuthMethod.describe("The authentication method"),
	identity: Identity.describe("The authenticated account or user identity"),
});
export type LoginOutput = z.infer<typeof authenticatedLoginOutput>;

const authorizationRequiredOutput = z.object({
	type: z.literal("authorization_required"),
	authorizationUrl: z
		.string()
		.describe("Open this URL in the user's browser to authorize the CLI"),
});
export type AuthorizationRequiredOutput = z.infer<
	typeof authorizationRequiredOutput
>;

export const loginOutput = z.union([
	authorizationRequiredOutput,
	authenticatedLoginOutput,
]);
export type LoginCommandOutput = z.infer<typeof loginOutput>;

export const loginExamples = [
	{ description: "Interactive login" },
	{
		options: { method: "oauth" },
		description: "Open a browser for OAuth login",
	},
	{
		options: { method: "api-key", apiKey: "whop_xxx" },
		description: "Non-interactive API-key login",
	},
	{
		options: { method: "api-key", apiKey: "whop_xxx", profile: "prod" },
		description: "Log in and name the profile",
	},
] satisfies Array<{
	description: string;
	options?: Partial<z.infer<typeof loginOptions>> | undefined;
}>;
export const loginHint =
	"Agents: run `login --method oauth --format jsonl` in the background with stdout redirected to a file (pipes like `| head` can buffer past the URL). " +
	"The first line is an authorization_required event — open its authorizationUrl in the user's browser yourself (e.g. `open <url>` on macOS, `xdg-open` on Linux). The URL is several KB long, so pass it programmatically and NEVER retype, truncate, or summarize it: a mangled scope list fails with invalid_scope. " +
	"Then wait for the process to exit; the final done event means the login saved. " +
	"If the user manages multiple businesses the login still succeeds without one — set it afterwards with `auth account <biz_id>` (use `auth account --list` to enumerate). " +
	"For unattended use, pass --api-key or set WHOP_API_KEY instead.";

// --- Context-free login flow ---
type LoginResult =
	| { ok: true; output: LoginOutput; existed: boolean; underscoped: boolean }
	| { ok: false; code: string; message: string };

function oauthSuccess(
	result: OAuthLoginResult,
): Extract<LoginResult, { ok: true }> {
	return {
		ok: true,
		existed: result.existed,
		underscoped: false,
		output: {
			profile: result.profileName,
			active: true,
			method: "oauth",
			identity: result.identity,
		},
	};
}

function oauthFailure(error: unknown): Extract<LoginResult, { ok: false }> {
	if (error instanceof OAuthError) {
		return { ok: false, code: error.code, message: error.message };
	}
	throw error;
}

export async function performLogin(input: {
	agent: boolean;
	method?: LoginMethod | undefined;
	apiKey?: string | undefined;
	profile?: string | undefined;
	accountId?: string | undefined;
	deferAccountSelection?: boolean | undefined;
}): Promise<LoginResult> {
	let method: AuthMethod = input.method === "oauth" ? "oauth" : "api_key";
	if (!input.method && !input.agent && !input.apiKey) {
		method = await chooseAuthMethod();
	}

	// Route to the OAuth browser flow.
	if (method === "oauth") {
		// Non-interactive OAuth can't surface the authorization URL from here —
		// only the streaming adapter can — so fail fast instead of silently
		// waiting out the callback timeout.
		if (input.agent) {
			return {
				ok: false,
				code: "OAUTH_STREAMING_REQUIRED",
				message:
					"OAuth login in agent mode must stream the authorization URL — use `login --method oauth --format jsonl`.",
			};
		}
		try {
			return oauthSuccess(
				await performOAuthLogin({
					profile: input.profile,
					accountId: input.accountId,
					deferAccountSelection: input.deferAccountSelection,
				}),
			);
		} catch (error) {
			return oauthFailure(error);
		}
	}

	// Resolve the API key: flag/env > interactive prompt > error in agent mode.
	let apiKey = input.apiKey;
	if (!apiKey) {
		if (input.agent) {
			return {
				ok: false,
				code: "API_KEY_REQUIRED",
				message: "No API key provided and not running interactively.",
			};
		}
		apiKey = await promptApiKey();
	}
	// Normalize: the SDK adds the `Bearer ` prefix itself, so strip one if pasted.
	const key = apiKey.replace(/^Bearer\s+/i, "").trim();

	// 3. Validate against the API.
	let result: Awaited<ReturnType<typeof validateApiKey>>;
	if (input.agent) {
		result = await validateApiKey(key, apiBaseUrl());
	} else {
		const s = startSpinner("Validating API key…");
		result = await validateApiKey(key, apiBaseUrl());
		const okay = result.status === "valid" || result.status === "underscoped";
		s.stop(okay ? "API key validated." : "API key check failed.");
	}
	if (result.status === "invalid") {
		return {
			ok: false,
			code: "INVALID_API_KEY",
			message: "That API key was rejected (401 Unauthorized).",
		};
	}
	if (result.status === "error") {
		return { ok: false, code: "VALIDATION_FAILED", message: result.message };
	}

	// 4. Persist (secret → keychain, metadata → config) and activate.
	const identity = result.identity;
	const name = input.profile ?? deriveProfileName(identity);
	const existed = listProfiles().some((profile) => profile.name === name);
	const profile = upsertProfile({ name, method, secret: key, identity });

	return {
		ok: true,
		existed,
		underscoped: result.status === "underscoped",
		output: { profile: profile.name, active: true, method, identity },
	};
}

// --- Shared adapters (used by the group command and the top-level alias) ---
type LoginAdapterCtx = {
	agent: boolean;
	format: "toon" | "json" | "yaml" | "md" | "jsonl";
	formatExplicit: boolean;
	options: {
		method?: LoginMethod | undefined;
		apiKey?: string | undefined;
		profile?: string | undefined;
		accountId?: string | undefined;
	};
	env: { WHOP_API_KEY?: string | undefined };
	ok: (
		data: LoginCommandOutput,
		meta?: { cta?: CtaInput | undefined },
	) => never;
	error: (options: ErrorInput) => never;
};

function loginSuccessMeta(result: Extract<LoginResult, { ok: true }>): {
	cta: CtaInput;
} {
	if (!getActiveProfile()?.accountId) {
		return {
			cta: {
				description:
					"You're logged in. Choose or create the business the CLI should use:",
				commands: [
					{
						command: "auth account",
						description:
							"Set the default business account (--list to see them)",
					},
					{
						command: "quickstart",
						description: "Choose or create a business account",
					},
				],
			},
		};
	}

	return {
		cta: {
			description: result.existed
				? "Updated and switched to this profile:"
				: "You're logged in:",
			commands: [
				{ command: "auth status", description: "Show the active identity" },
				{
					command: "quickstart",
					description: "Choose or create a business account",
				},
			],
		},
	};
}

function failLogin(
	c: LoginAdapterCtx,
	result: Extract<LoginResult, { ok: false }>,
): never {
	let cta: CtaInput | undefined;
	if (result.code === "API_KEY_REQUIRED") {
		cta = {
			description: "Choose a non-interactive login method:",
			commands: [
				{
					command: "auth login",
					options: { method: "oauth" },
					description: "Use OAuth (add --format jsonl for agents)",
				},
				{
					command: "auth login",
					options: { method: "api-key", apiKey: true },
					description: "Pass --api-key <key>",
				},
			],
		};
	} else if (result.code === "ACCOUNT_NOT_FOUND") {
		cta = {
			description: "Choose which business account to use:",
			commands: [
				{
					command: "auth login",
					options: { method: "oauth", accountId: true },
					description: "Pass --account-id biz_xxx",
				},
			],
		};
	}
	return c.error({
		code: result.code,
		message: result.message,
		retryable: result.code !== "INVALID_API_KEY",
		cta,
	});
}

async function standardLoginAdapter(c: LoginAdapterCtx): Promise<never> {
	let result: LoginResult;
	try {
		result = await performLogin({
			agent: c.agent,
			method: c.options.method,
			apiKey: c.options.apiKey ?? c.env.WHOP_API_KEY,
			profile: c.options.profile,
			accountId: c.options.accountId,
		});
	} catch (error) {
		if (error instanceof PromptCancelledError) {
			return c.error({
				code: "CANCELLED",
				message: "Login cancelled.",
				exitCode: 130,
			});
		}
		throw error;
	}

	if (!result.ok) return failLogin(c, result);
	return c.ok(result.output, loginSuccessMeta(result));
}

/**
 * Agent OAuth login as a stream: emit the authorization URL as soon as the
 * local callback listener is ready (so the agent can show it to the user),
 * then the authenticated profile once the browser round-trip completes.
 */
async function* streamingOAuthLoginAdapter(
	c: LoginAdapterCtx,
	oauthDependencies?: OAuthLoginDependencies,
): AsyncGenerator<LoginCommandOutput, never, unknown> {
	let login: Awaited<ReturnType<typeof prepareOAuthLogin>>;
	try {
		login = await prepareOAuthLogin(
			{
				profile: c.options.profile,
				interactive: false,
				accountId: c.options.accountId,
			},
			oauthDependencies,
		);
	} catch (error) {
		return failLogin(c, oauthFailure(error));
	}

	yield {
		type: "authorization_required",
		authorizationUrl: login.authorizationUrl,
	};

	let result: Extract<LoginResult, { ok: true }>;
	try {
		result = oauthSuccess(await login.complete());
	} catch (error) {
		return failLogin(c, oauthFailure(error));
	}

	yield result.output;
	return c.ok(result.output, loginSuccessMeta(result));
}

export function loginAdapter(
	c: LoginAdapterCtx,
	oauthDependencies?: OAuthLoginDependencies,
): Promise<never> | AsyncGenerator<LoginCommandOutput, never, unknown> {
	if (c.options.method === "oauth" && c.options.apiKey) {
		return c.error({
			code: "CONFLICTING_OPTIONS",
			message: "--method oauth cannot be combined with --api-key.",
			retryable: true,
		});
	}
	if (c.options.method === "oauth" && (c.agent || c.format === "jsonl")) {
		// incur buffers explicit non-jsonl formats until the generator finishes,
		// which would hide the authorization URL until after the callback wait.
		if (c.formatExplicit && c.format !== "jsonl") {
			return c.error({
				code: "OAUTH_STREAMING_FORMAT_REQUIRED",
				message:
					"OAuth login requires --format jsonl so the authorization URL is shown before the callback wait.",
				retryable: true,
				cta: {
					commands: ["auth login --method oauth --format jsonl"],
				},
			});
		}
		return streamingOAuthLoginAdapter(c, oauthDependencies);
	}
	return standardLoginAdapter(c);
}
